
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, InfoIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ComplianceSettingsProps {
  onSave: () => void;
}

interface ReportConfig {
  id: string;
  state: string;
  report_format: string;
  submission_method: string;
  submission_url: string | null;
  submission_deadline_days: number;
  additional_requirements: string | null;
  created_at: string;
  updated_at: string;
  active_from: string;
  active_to: string | null;
}

export function ComplianceSettings({ onSave }: ComplianceSettingsProps) {
  const [configs, setConfigs] = useState<ReportConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<ReportConfig | null>(null);
  const [selectedState, setSelectedState] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // US States
  const states = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", 
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", 
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", 
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", 
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];
  
  const reportFormats = ["CSV", "PDF", "Excel", "Online Form", "API Submission"];
  const submissionMethods = ["Email", "Web Portal", "Mail", "Fax", "API"];

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('report_configurations')
          .select('*');
        
        if (error) throw error;
        setConfigs(data || []);
      } catch (error) {
        console.error('Error fetching report configurations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, []);
  
  // When state is selected, find its config
  useEffect(() => {
    if (selectedState) {
      const config = configs.find(c => c.state === selectedState);
      setSelectedConfig(config || createEmptyConfig(selectedState));
    } else {
      setSelectedConfig(null);
    }
  }, [selectedState, configs]);
  
  // Create empty config for a state
  const createEmptyConfig = (state: string): ReportConfig => ({
    id: '',
    state: state,
    report_format: 'PDF',
    submission_method: 'Email',
    submission_url: '',
    submission_deadline_days: 30,
    additional_requirements: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    active_from: new Date().toISOString(),
    active_to: null
  });
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConfig) return;
    
    try {
      setIsSubmitting(true);
      
      if (selectedConfig.id) {
        // Update existing config
        const { error } = await supabase
          .from('report_configurations')
          .update({
            report_format: selectedConfig.report_format,
            submission_method: selectedConfig.submission_method,
            submission_url: selectedConfig.submission_url,
            submission_deadline_days: selectedConfig.submission_deadline_days,
            additional_requirements: selectedConfig.additional_requirements,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedConfig.id);
          
        if (error) throw error;
      } else {
        // Insert new config
        const { error } = await supabase
          .from('report_configurations')
          .insert({
            state: selectedConfig.state,
            report_format: selectedConfig.report_format,
            submission_method: selectedConfig.submission_method,
            submission_url: selectedConfig.submission_url,
            submission_deadline_days: selectedConfig.submission_deadline_days,
            additional_requirements: selectedConfig.additional_requirements
          });
          
        if (error) throw error;
        
        // Refresh the configs to get the newly created one
        const { data } = await supabase
          .from('report_configurations')
          .select('*');
        
        setConfigs(data || []);
      }
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      onSave();
    } catch (error) {
      console.error('Error saving report configuration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleInputChange = (field: string, value: string | number) => {
    if (!selectedConfig) return;
    
    setSelectedConfig(prev => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  };
  
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Loading compliance settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>State Reporting Requirements</CardTitle>
          <CardDescription>
            Configure compliance reporting requirements for each state
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <Label htmlFor="state-select">Select State</Label>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent>
                {states.map(state => (
                  <SelectItem key={state} value={state}>
                    {state} {configs.some(c => c.state === state) && 
                      <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-600">Configured</Badge>
                    }
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedConfig && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {showSuccess && (
                <Alert className="bg-green-50 border-green-200 mb-4">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Success</AlertTitle>
                  <AlertDescription className="text-green-700">
                    {selectedConfig.id ? 'Configuration updated successfully.' : 'New configuration created.'}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="report-format">Report Format</Label>
                  <Select 
                    value={selectedConfig.report_format} 
                    onValueChange={(value) => handleInputChange('report_format', value)}
                  >
                    <SelectTrigger id="report-format">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      {reportFormats.map(format => (
                        <SelectItem key={format} value={format}>{format}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="submission-method">Submission Method</Label>
                  <Select 
                    value={selectedConfig.submission_method} 
                    onValueChange={(value) => handleInputChange('submission_method', value)}
                  >
                    <SelectTrigger id="submission-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {submissionMethods.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="submission-url">Submission URL/Email</Label>
                <Input 
                  id="submission-url"
                  value={selectedConfig.submission_url || ''}
                  onChange={(e) => handleInputChange('submission_url', e.target.value)}
                  placeholder="https://example.gov/submission or agency@example.gov"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deadline-days">Submission Deadline (Days)</Label>
                <Input 
                  id="deadline-days"
                  type="number"
                  value={selectedConfig.submission_deadline_days}
                  onChange={(e) => handleInputChange('submission_deadline_days', parseInt(e.target.value, 10))}
                  min={1}
                  max={365}
                />
                <p className="text-xs text-muted-foreground">
                  Number of days after quarter/period end when the report is due
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="additional-requirements">Additional Requirements</Label>
                <Input 
                  id="additional-requirements"
                  value={selectedConfig.additional_requirements || ''}
                  onChange={(e) => handleInputChange('additional_requirements', e.target.value)}
                  placeholder="Any additional state-specific requirements"
                />
              </div>
              
              <Alert variant="default" className="bg-blue-50 border-blue-200 mt-4">
                <InfoIcon className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">Report Scheduling</AlertTitle>
                <AlertDescription className="text-blue-700">
                  Automatic reports will be scheduled based on these settings for {selectedState} and will appear in your compliance dashboard.
                </AlertDescription>
              </Alert>
              
              <div className="flex justify-end pt-4">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </form>
          )}
          
          {!selectedState && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Select a state to configure its reporting requirements</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
